import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { CartItem } from "../types";

type CartContextValue = {
  items: CartItem[];
  count: number;
  totalCents: number;
  open: boolean;
  addItem: (item: Omit<CartItem, "quantity">, quantity?: number) => void;
  updateQuantity: (id: string, kind: CartItem["kind"], quantity: number) => void;
  removeItem: (id: string, kind: CartItem["kind"]) => void;
  clear: () => void;
  setOpen: (open: boolean) => void;
};

const CartContext = createContext<CartContextValue | null>(null);
const storageKey = "ojs-bistro-cart";

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const raw = window.localStorage.getItem(storageKey);
    if (raw) setItems(JSON.parse(raw) as CartItem[]);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(storageKey, JSON.stringify(items));
  }, [items]);

  const value = useMemo<CartContextValue>(() => {
    const totalCents = items.reduce((sum, item) => sum + item.priceCents * item.quantity, 0);
    const count = items.reduce((sum, item) => sum + item.quantity, 0);
    return {
      items,
      count,
      totalCents,
      open,
      addItem: (item, quantity = 1) => {
        setItems((current) => {
          const existing = current.find((entry) => entry.id === item.id && entry.kind === item.kind);
          if (!existing) return [...current, { ...item, quantity }];
          return current.map((entry) =>
            entry.id === item.id && entry.kind === item.kind
              ? { ...entry, quantity: entry.quantity + quantity }
              : entry
          );
        });
        setOpen(true);
      },
      updateQuantity: (id, kind, quantity) => {
        setItems((current) =>
          current
            .map((item) => (item.id === id && item.kind === kind ? { ...item, quantity } : item))
            .filter((item) => item.quantity > 0)
        );
      },
      removeItem: (id, kind) => {
        setItems((current) => current.filter((item) => item.id !== id || item.kind !== kind));
      },
      clear: () => setItems([]),
      setOpen
    };
  }, [items, open]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const value = useContext(CartContext);
  if (!value) throw new Error("useCart must be used inside CartProvider");
  return value;
}
